<script lang="ts" setup>
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import { computed, ref, useTemplateRef, watch } from "vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import { useSnackbarStore } from "@/store/snackbar.store";
import { API } from "@/api/api";
import DateTimePicker from "@/components/pickers/DateTimePicker.vue";
import { DialogSize } from "@/constants/dialog";
import type { VForm } from "vuetify/components";
import { useAppStore } from "@/store/app.store";
import { useDateTime } from "@/composables";
import dayjs from "dayjs";

const api = new API();

const isShareDialog = ref<boolean>(false);
const date = ref<string>("");

const props = defineProps<{
  truck: TruckDto;
}>();

const emits = defineEmits<{
  (e: "update-truck"): void;
}>();

const { showSnackbarError, showSnackbarSuccess } = useSnackbarStore();
const { getTZNames, nowTZ, getShortTZForDate, dateToUTCSystemFormat } = useDateTime();
const { dispatcher } = useAppStore();
const loading = ref<boolean>(false);
const link = ref<string | null>(null);
const form = useTemplateRef<VForm | null>("form");
const isEnableTracking = ref<boolean>(props.truck.enableTracking);

async function handleLink() {
  if (link.value) {
    link.value = null;

    return;
  }

  if (!props.truck.id || !form.value) {
    return;
  }

  const result = await form.value.validate();

  if (!result.valid || !dispatcher?.timezone) {
    return;
  }

  loading.value = true;

  try {
    const dateInUTC = dateToUTCSystemFormat(date.value, dispatcher.timezone);
    const response = await api.trucks.getTrackLink(props.truck.id, dateInUTC);

    if (response.success && response.data?.link) {
      link.value = response.data.link;
    }
  } catch (error) {
    showSnackbarError("Could not create tracking");
  }

  loading.value = false;
}

async function copyLink() {
  if (link.value) {
    await navigator.clipboard.writeText(link.value);
    showSnackbarSuccess("Tracking link copied to clipboard");
  }
}

const onChangeTrackingError = () => {
  showSnackbarError("Could not update enable tracking");
  isEnableTracking.value = props.truck.enableTracking;
};

async function handleEnableTracking() {
  if (!props.truck.id) {
    return;
  }

  try {
    const response = await api.trucks.handleEnableTracking(props.truck.id, isEnableTracking.value);

    if (!response.success) {
      onChangeTrackingError();
    } else {
      const statusLabel = isEnableTracking.value ? "active" : "not active";
      showSnackbarSuccess(`External tracking status has changed to ${statusLabel}`);
    }
  } catch (error) {
    onChangeTrackingError();
  }
}

const minDate = computed(() => {
  if (!dispatcher?.timezone) {
    return null;
  }

  return nowTZ(dispatcher.timezone);
});

const timeLabel = computed(() => {
  let timezone = "";

  if (!dispatcher?.timezone) {
    return "Expiration time";
  }

  if (date.value) {
    const dt = dayjs(date.value).toDate();
    timezone = getShortTZForDate(dt, dispatcher.timezone);
  } else {
    timezone = getTZNames(dispatcher.timezone);
  }

  return `Expiration time (${timezone})`;
});

watch(isShareDialog, (value) => {
  if (value) {
    isEnableTracking.value = props.truck.enableTracking;
  }

  if (!value && isEnableTracking.value !== props.truck.enableTracking) {
    emits("update-truck");
  }

  if (!value) {
    date.value = "";
  }
});
</script>

<template>
  <div>
    <v-btn
      class="text-uppercase w-100 mt-3 font-weight-500"
      color="primary"
      variant="outlined"
      @click="isShareDialog = true"
    >
      External tracking settings
    </v-btn>

    <omni-dialog
      v-if="isShareDialog"
      v-model="isShareDialog"
      :size="DialogSize.large"
      hide-actions
      title="External tracking settings"
    >
      <v-form ref="form">
        <v-switch
          v-model="isEnableTracking"
          class="text-grey-darken-2"
          color="primary"
          label="External truck tracking for the customer"
          @update:model-value="handleEnableTracking"
        ></v-switch>

        <span class="text-grey-darken-2">Select expiration date for the link</span>

        <div class="d-flex align-center justify-space-between mt-4 mb-4">
          <div class="w-100 d-flex flex-wrap">
            <date-time-picker
              v-model="date"
              :min="minDate"
              :time-label="timeLabel"
              date-class="w-100"
              date-label="Expiration date"
              required
            />
          </div>

          <v-btn
            :disabled="!date"
            :height="link ? 44 : 40"
            :loading="loading"
            :variant="link ? 'outlined' : 'flat'"
            class="text-uppercase ml-5"
            color="primary"
            width="130"
            @click="handleLink"
          >
            {{ link ? "Update" : "Create link" }}
          </v-btn>
        </div>

        <template v-if="link">
          <span class="font-size-14 text-grey-darken-2">Link for broker</span>

          <div
            class="d-flex justify-space-between align-center mt-4 py-3 px-5 bg-grey-lighten-4 rounded link-container"
          >
            <span
              class="text-grey-darken-2 font-weight-500 link white-space-nowrap overflow-hidden text-overflow-ellipsis"
              >{{ link }}</span
            >
            <v-btn class="text-uppercase ml-3" color="primary" variant="flat" @click="copyLink">Copy link</v-btn>
          </div>
        </template>
      </v-form>
    </omni-dialog>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.link-container {
  border: 1px solid $grey-lighten-2;
  background-color: $grey-lighten-4;
}
</style>
